/*
 * <reviews-rating-bars> — repairs the star-rating breakdown bars inside the
 * Reviews.io "highlights" panel (.SentimentWidget--embeddedInsideWidget), which
 * render permanently EMPTY on this store.
 *
 * Wrap the container that holds the Reviews.io @app block (keep its class):
 *
 *   <reviews-rating-bars class="pdp-reviews__content">
 *     <div class="pdp-reviews__app-block">…</div>
 *   </reviews-rating-bars>
 *
 * WHAT IS BROKEN, AND WHY IT IS THEIRS NOT OURS
 * --------------------------------------------
 * The bar is not a width — it is a MASK. Per elements-widget.css:
 *
 *   .R-SliderIndicator--style-filledbar .R-SliderIndicator__inner  { overflow: hidden }
 *   .R-SliderIndicator--style-filledbar .R-SliderIndicator__bg     { background: var(--slider-indicator-button-color) }
 *   .R-SliderIndicator--style-filledbar .R-SliderIndicator__button { width: 120%; background: var(--slider-indicator-bg-color) }
 *
 * `__bg` is the FILL, painted edge to edge. `__button` is a 120%-wide slab in the
 * TRACK colour that slides over it, so the inline `left` on `__button` IS the
 * fill percentage: left:100% = full bar, left:0% = empty bar.
 *
 * Reviews.io renders `left: 0%` and animates to the real value only when its
 * `barsAnimated` flag flips (polaris/196/1958.build.js, component `ai-summary`):
 *
 *   data:  { ratingExpanded: false, barsAnimated: false, prevHasSummaries: null }
 *   watch: {
 *     sentimentData: { immediate: true, handler() {          // ← runs during watcher SETUP
 *       if (this.hasSummaries !== this.prevHasSummaries) {
 *         this.ratingExpanded = !this.hasSummaries           // no AI summary → start expanded
 *         …
 *     }},
 *     ratingExpanded(v) { if (v) { …requestAnimationFrame(() => this.barsAnimated = true) } }
 *   }
 *
 * `sentimentData` is declared BEFORE `ratingExpanded` and carries
 * `immediate: true`, so its first run happens before the `ratingExpanded` watcher
 * exists. On a product with no AI summary that first run is the only thing that
 * ever sets `ratingExpanded` — the watcher that would animate the bars is never
 * notified, `barsAnimated` stays false, and every bar sits at `left: 0%` for the
 * life of the page. The panel then reads "0 · 0%" on all five rows even when the
 * labels beside them say otherwise ("5 · 83%" measured on
 * blaze-and-the-monster-machine-race-track-set, 2026-08-21).
 *
 * The panel is also the ONLY escape hatch — and it is shut. The trigger row is
 * click/keyup-guarded on `hasSummaries`, and gets `role`/`tabindex` only when that
 * is true, so with no summary there is nothing to toggle. Verified live: the
 * trigger row carries no role attribute on either theme.
 *
 * AI summaries need a review volume this catalogue does not have (83.6% of SKUs
 * have zero reviews — project_reviewsio_api_integration), so "wait for Reviews.io
 * to generate a summary" is not a fix. Nor is CSS: the percentage exists only in
 * the label text, and CSS cannot read text.
 *
 * WHY IT WRITES `left` RATHER THAN REACHING INTO VUE
 * -------------------------------------------------
 * `barsAnimated` lives on a component instance in a production Vue 3 build —
 * `__vueParentComponent` is a dev-only property, so there is no supported handle.
 * Writing the inline `left` the widget itself writes uses their own contract, and
 * their own `transition: left .8s` + staggered `transitionDelay` still run, so the
 * bars animate exactly as designed.
 *
 * Vue does not fight back. Its style patch diffs prev-vnode against next-vnode;
 * `left` is `'0%'` in both, so a re-render never rewrites the property and our
 * value survives. A destroy/recreate (filter, sort, page change) does produce
 * fresh `left: 0%` nodes — that is what the MutationObserver is for.
 *
 * SOURCE OF TRUTH is the PERCENT in the row's own label, "<count> · <percent>%".
 *
 * Recomputing from the counts instead — count / sum-of-counts, which reproduces
 * their unrounded `bucketPercent()` — looks more precise and is wrong. Their
 * denominator is `filteredNumberOfItems`, NOT the sum of the buckets, and the two
 * diverge the moment anyone touches the overall-rating filter: the buckets keep
 * their unfiltered sizes while the total drops to the filtered count. Measured on
 * blaze-and-the-monster-machine-race-track-set (6 reviews: five 5★, one 2★):
 *
 *   unfiltered        5 · 83%    1 · 17%     (sums to 100%)
 *   filtered to 5★    5 · 100%   1 · 20%     (sums to 120%)
 *   filtered to 2★    5 · 500%   1 · 100%    (sums to 600%)
 *
 * So the label is the only thing a bar can agree with, and agreeing with the
 * number printed beside it is the whole job. The cost is their whole-number
 * rounding (`bucketDisplayPercents`, largest-remainder): 83% against a true
 * 83.33% is ~1px on a 300px rail, and it buys exactness against the label.
 *
 * The 500% row is why the value is clamped. Reviews.io's own bar would slide the
 * mask five rails to the right; clamped, an over-100% bucket renders full, which
 * is the same picture with a sane inline value.
 */
if (!customElements.get('reviews-rating-bars')) {
  class ReviewsRatingBars extends HTMLElement {
    connectedCallback() {
      /*
       * The @app block hydrates async, so the panel is almost never in the DOM
       * at connect time — hence childList/subtree. `attributeFilter: ['style']`
       * covers the other half: a destroy-and-recreate (filter, sort, page change)
       * hands back fresh nodes at `left: 0%`.
       *
       * This observer necessarily sees its OWN writes. It terminates because
       * apply() compares before writing, so the pass triggered by a write finds
       * nothing to change — one extra callback, not a loop. Verified: 0 style
       * writes over 4s idle.
       */
      this.observer = new MutationObserver(this.schedule.bind(this));
      this.observer.observe(this, {
        childList: true,
        subtree: true,
        attributes: true,
        attributeFilter: ['style'],
      });

      this.apply();
    }

    disconnectedCallback() {
      this.observer?.disconnect();
      if (this.frame) cancelAnimationFrame(this.frame);
    }

    /* Coalesce a burst of mutations (a re-render touches dozens of nodes) into
       one pass on the next frame. */
    schedule() {
      if (this.frame) return;
      this.frame = requestAnimationFrame(() => {
        this.frame = null;
        this.apply();
      });
    }

    apply() {
      const masks = this.querySelectorAll(
        '.SentimentWidget__breakdownContent .R-SliderIndicator__button'
      );

      masks.forEach((mask) => {
        const percent = this.readPercent(mask);
        if (percent === null) return;

        const value = Math.min(100, Math.max(0, percent));

        /*
         * Compare NUMBERS, not strings — this is what stops the observer feeding
         * itself, and a string compare would not: the CSSOM makes no promise to
         * hand back the exact text we wrote, so `style.left !== '83.33%'` could
         * stay true forever and spin one write per frame. The 0.05% tolerance
         * also leaves an untouched `left: 0%` alone on a genuinely empty row.
         */
        const current = parseFloat(mask.style.left);
        if (!Number.isFinite(current) || Math.abs(current - value) > 0.05) {
          mask.style.left = `${value.toFixed(2)}%`;
        }
      });
    }

    /*
     * The label sits in a sibling cell of the row: "<count> · <percent>%".
     * Matched by CONTENT ('%'), not by the `.u-textRight--all` utility class it
     * happens to sit in — utility classes are the most churn-prone handle in the
     * widget. `.cssVar-textBody` is the innermost element Reviews.io wraps every
     * widget string in; the row's other one holds the bare star number.
     */
    readPercent(mask) {
      const row = mask.closest('.R-flex-row');
      const cell = row
        ? [...row.querySelectorAll('.cssVar-textBody')].find((el) =>
            el.textContent.includes('%')
          )
        : null;
      if (!cell) return null;

      const match = cell.textContent.match(/(\d+(?:[.,]\d+)?)\s*%/);
      return match ? parseFloat(match[1].replace(',', '.')) : null;
    }
  }

  customElements.define('reviews-rating-bars', ReviewsRatingBars);
}
